import React, { useEffect, useMemo, useState, useRef } from "react";
import "./Registration.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ---------------- tiny, dependency-free number animation ---------------- */
function CountUp({ to = 0, duration = 900, prefix = "", suffix = "" }) {
  const [val, setVal] = useState(0);
  const startTs = useRef(null);
  const target = Number(to || 0);

  useEffect(() => {
    startTs.current = null;
    let raf = 0;

    const step = (ts) => {
      if (!startTs.current) startTs.current = ts;
      const p = Math.min(1, (ts - startTs.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  const formatted = Math.round(val).toLocaleString();
  return (
    <>
      {prefix}
      {formatted}
      {suffix}
    </>
  );
}

export default function Registration() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState({
    totalRegistrations: 0,
    pendingApprovals: 0,
    totalTravelers: 0,
    totalGuides: 0,
    totalHotelOwners: 0,
    totalTravelerAgents: 0,
  });
  const [rows, setRows] = useState([]);
  const [onlyPending, setOnlyPending] = useState(false);

  // new filters
  const [role, setRole] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [fetching] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const toggleAgentInfo = async (userId, travelerAgentDetailsId) => {
    if (expandedRow && expandedRow.userId === userId) {
      setExpandedRow(null);
    } else {
      try {
        const res = await fetch(`${API}/agents/${travelerAgentDetailsId}`);
        const data = await res.json();
        if (!res.ok)
          throw new Error(data?.message || "Failed to fetch agent info.");
        setExpandedRow({ userId, data });
      } catch (e) {
        console.error(e);
        alert(e.message || "Failed to fetch agent info.");
      }
    }
  };

  // Cards shown at the top
  const cards = useMemo(
    () => [
      {
        title: "Total Registrations",
        value: stats.totalRegistrations,
        sub: "All users",
        icon: "📝",
      },
      {
        title: "Pending Approvals",
        value: stats.pendingApprovals,
        sub: "Pending Registrations",
        icon: "🕐",
        prefix: "+",
      },
      {
        title: "Total Travelers",
        value: stats.totalTravelers,
        sub: "Travellers",
        icon: "🙍",
        prefix: "+",
      },
      {
        title: "Total Guides",
        value: stats.totalGuides,
        sub: "Guides",
        icon: "🧭",
        prefix: "+",
      },
      {
        title: "Total Hotel Owners",
        value: stats.totalHotelOwners,
        sub: "Hotel Owners",
        icon: "🏨",
        prefix: "+",
      },
      {
        title: "Total Traveler Agents",
        value: stats.totalTravelerAgents,
        sub: "Traveler Agents",
        icon: "💼",
        prefix: "+",
      },
    ],
    [stats]
  );

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setError("");

        const headers = getAuthHeaders();
        const [summaryRes, listRes] = await Promise.all([
          fetch(`${API}/admin/registrations/summary`, { headers }),
          fetch(`${API}/admin/registrations`, { headers }),
        ]);

        const summary = await summaryRes.json();
        const list = await listRes.json();

        if (!summaryRes.ok)
          throw new Error(summary?.message || "Failed to fetch summary.");
        if (!listRes.ok)
          throw new Error(list?.message || "Failed to fetch registrations.");

        if (!mounted) return;

        setStats({
          totalRegistrations: Number(summary.totalRegistrations) || 0,
          pendingApprovals: Number(summary.pendingApprovals) || 0,
          totalTravelers: Number(summary.totalTravelers) || 0,
          totalGuides: Number(summary.totalGuides) || 0,
          totalHotelOwners: Number(summary.totalHotelOwners) || 0,
          totalTravelerAgents: Number(summary.totalTravelerAgents) || 0,
        });

        setRows(
          (Array.isArray(list) ? list : []).map((r) => ({
            ...r,
            dateText: r.date ? new Date(r.date).toLocaleDateString() : "—",
            rawDate: r.date ? new Date(r.date) : null,
          }))
        );
      } catch (e) {
        if (mounted) setError(e.message || "Load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const prevRowsRef = useRef(rows);

  useEffect(() => {
    const prevRows = prevRowsRef.current;
    if (prevRows.length > 0 && rows.length > 0) {
      rows.forEach((row) => {
        const prevRow = prevRows.find((p) => p.userId === row.userId);
        if (
          prevRow &&
          prevRow.status === "Pending" &&
          row.status === "Approved"
        ) {
          const isAgent = row.role === "Traveler Agent" || row.role === "Agent" || (row.role && row.role.includes("Agent"));

          if (isAgent) {
            fetch(`${API}/admin/registrations/send-agent-activation-email`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ userEmail: row.email, roleName: "Traveler Agent" }),
            }).catch((e) =>
              console.error("Failed to send agent activation email:", e)
            );
          } else {
            fetch(`${API}/admin/registrations/send-activation-email`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ user: row }),
            }).catch((e) =>
              console.error("Failed to send activation email:", e)
            );
          }
        }
      });
    }
    prevRowsRef.current = rows;
  }, [rows]);

  // filter in-memory
  const filtered = useMemo(() => {
    let data = onlyPending ? rows.filter((r) => r.status === "Pending") : rows;

    if (role) {
      if (role === "Traveler Agent" || role === "Agent") {
        data = data.filter((r) => r.role === "Traveler Agent" || r.role === "Agent" || (r.role && r.role.includes("Agent")));
      } else {
        data = data.filter((r) => r.role === role);
      }
    }

    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      data = data.filter((r) => r.rawDate && r.rawDate >= fromDate);
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      data = data.filter((r) => r.rawDate && r.rawDate <= toDate);
    }

    return data;
  }, [rows, onlyPending, role, dateFrom, dateTo]);

  async function toggleStatus(userId) {
    try {
      setRows((prev) =>
        prev.map((r) =>
          r.userId === userId
            ? { ...r, status: r.status === "Approved" ? "Pending" : "Approved" }
            : r
        )
      );

      const res = await fetch(`${API}/admin/registrations/${userId}/status`, {
        method: "PATCH",
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update status.");

      setRows((prev) =>
        prev.map((r) =>
          r.userId === userId
            ? {
              ...r,
              status: data.row.status,
              fullName: data.row.fullName,
              role: data.row.role,
              nic: data.row.nic,
              phone: data.row.phone,
              email: data.row.email,
              dateText: data.row.date
                ? new Date(data.row.date).toLocaleDateString()
                : "—",
              rawDate: data.row.date ? new Date(data.row.date) : null,
            }
            : r
        )
      );

      setStats({
        totalRegistrations: Number(data.summary.totalRegistrations) || 0,
        pendingApprovals: Number(data.summary.pendingApprovals) || 0,
        totalTravelers: Number(data.summary.totalTravelers) || 0,
        totalGuides: Number(data.summary.totalGuides) || 0,
        totalHotelOwners: Number(data.summary.totalHotelOwners) || 0,
        totalTravelerAgents: Number(data.summary.totalTravelerAgents) || 0,
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Failed to update status.");
    }
  }

  /* ------------ loading skeleton like Withdrawal.js ------------ */
  if (loading) {
    return (
      <div className="reg-page">
        <div className="section-head">
          <div>
            <h2 className="h2">Registration</h2>
            <p className="muted">Loading…</p>
          </div>
        </div>
        <section className="cards">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card stat" style={{ opacity: 0.6 }}>
              <div className="stat-meta">
                <div className="stat-title">Loading…</div>
                <div className="stat-value">—</div>
                <div className="stat-sub">—</div>
              </div>
            </div>
          ))}
        </section>
        <section
          className="card table-card"
          style={{ padding: 24, color: "gray" }}
        >
          Loading registrations…
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reg-page">
        <div className="section-head">
          <div>
            <h2 className="h2">Registration</h2>
            <p className="muted" style={{ color: "crimson" }}>
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reg-page">
      {/* Top stat cards */}
      <section className="cards">
        {cards.map((s, i) => (
          <div className="card stat" key={i}>
            <div className="lock" aria-hidden>
              {s.icon}
            </div>
            <div className="stat-meta">
              <div className="stat-title">{s.title}</div>
              <div className="stat-value">
                <CountUp to={s.value} prefix={s.prefix || ""} />
              </div>
              <div className="stat-sub">{s.sub}</div>
            </div>
          </div>
        ))}
      </section>

      {/* Header + filters styled like Withdrawal.js */}
      <section className="section-head">
        <div>
          <h2 className="h2">Registration</h2>
          <p className="muted">
            Manage owner, guide, and traveler registrations
          </p>
        </div>

        <div
          className="filters"
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="input"
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              color: role ? "inherit" : "var(--muted)",
            }}
            title="Filter by role"
          >
            <option value="">All Roles</option>
            <option value="Traveler">Traveler</option>
            <option value="Guide">Guide</option>
            <option value="Hotel Owner">Hotel Owner</option>
            <option value="Traveler Agent">Traveler Agent</option>
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="input"
            title="From (registered date)"
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              color: role ? "inherit" : "var(--muted)",
            }}
          />
          <span className="muted">to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="input"
            title="To (registered date)"
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              color: role ? "inherit" : "var(--muted)",
            }}
          />

          {fetching ? (
            <span className="muted" style={{ fontSize: 12 }}>
              Updating…
            </span>
          ) : null}

          <div className="segmented">
            <button
              className={`seg ${onlyPending ? "" : "active"}`}
              onClick={() => setOnlyPending(false)}
            >
              All
            </button>
            <button
              className={`seg ${onlyPending ? "active" : ""}`}
              onClick={() => setOnlyPending(true)}
            >
              Pending
            </button>
          </div>
        </div>
      </section>

      {/* Table */}
      <div className="card table-card">
        <table className="table">
          <thead>
            <tr>
              <th>Registered ID</th>
              <th>Full Name</th>
              <th>User Role</th>
              <th>NIC</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Registered Date</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <React.Fragment key={r.userId}>
                <tr>
                  <td className="id-cell">
                    <span className="id-badge">{r.regId}</span>
                  </td>
                  <td>{r.fullName}</td>
                  <td>{r.role}</td>
                  <td className="muted">{r.nic}</td>
                  <td className="muted">{r.phone}</td>
                  <td className="muted">{r.email}</td>
                  <td className="muted nowrap">{r.dateText}</td>
                  <td>
                    <button
                      className={
                        r.status === "Approved"
                          ? "badge green as-button"
                          : "badge amber as-button"
                      }
                      onClick={() => toggleStatus(r.userId)}
                    >
                      {r.status}
                    </button>
                  </td>
                  <td>
                    {r.traveler_agent_details_id ? (
                      <button
                        className="badge blue"
                        onClick={() =>
                          toggleAgentInfo(r.userId, r.traveler_agent_details_id)
                        }
                        title="View Agent Info"
                      >
                        Info
                      </button>
                    ) : (
                      <span className="dots">⋯</span>
                    )}
                  </td>
                </tr>
                {expandedRow && expandedRow.userId === r.userId && (
                  <tr>
                    <td colSpan="9">
                      <div className="agent-info-dropdown">
                        <strong>Agent Info:</strong>
                        <p>Name: {expandedRow.data.full_name}</p>
                        <p>Email: {expandedRow.data.email}</p>
                        <p>Phone: {expandedRow.data.phone_number}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
